// Handles modifying state in response to actions

import { createReducer, on } from '@ngrx/store';
import {
  appendCharacter,
  clearSession,
  completeTypingMessage,
  loadSession,
  loadSourceDocuments,
  populateMessages,
  receiveMessage,
  saveSession,
  sendMessage,
  setLoading,
  updateUserPrompt
} from './conversations.actions';
import { SourceDocument } from 'src/app/models/advisor.interface';
import { ChatMessage } from 'src/app/models/conversations.interface';

export interface ConversationState {
  id: string | null;
  collection_name: string | null;
  collection_id: string | null;
  replyFromLLM: ChatMessage | null;
  userPrompt: string;
  isTyping: boolean;
  messages: ChatMessage[];
  loading: boolean;
  error: string | null;
  sourceDocuments: SourceDocument[] | null;
  selected_collections_id: string[];
}

export const initialState: ConversationState = {
  id: null,
  collection_id: null,
  collection_name: null,
  messages: [],
  isTyping: false,
  replyFromLLM: null,
  userPrompt: '',
  loading: false,
  error: null,
  sourceDocuments: null,
  selected_collections_id: []
};

export const conversationReducer = createReducer(
  initialState,
  // Navigation away, stores state into localstorage
  on(saveSession, (state, { session_id, collection_id, collection_name, selected_collections_id }) => {
    console.log('saving', collection_id, session_id);
    localStorage.setItem('collection_id', collection_id);
    localStorage.setItem('session_id', session_id);
    localStorage.setItem('collection_name', collection_name);
    localStorage.setItem('selected_collections_id', JSON.stringify(selected_collections_id));

    return {
      ...state,
      id: session_id,
      collection_id,
      collection_name,
      selected_collections_id
    };
  }),
  on(loadSession, (state, { session_id, collection_id, collection_name, selected_collections_id }) => ({
    ...state,
    id: session_id,
    collection_id: collection_id,
    collection_name: collection_name,
    selected_collections_id: selected_collections_id
  })),
  on(setLoading, (state, { loading }) => ({
    ...state,
    loading: loading
  })),
  on(clearSession, state => {
    localStorage.removeItem('collection_id');
    localStorage.removeItem('sessionId');
    localStorage.removeItem('selected_collections_id');
    return {
      ...state,
      id: null,
      collection_id: null,
      messages: [],
      replyFromLLM: null,
      userPrompt: '',
      isTyping: false,
      loading: false,
      error: null
    };
  }),
  // Load messages
  on(populateMessages, (state, { session_id, messages, collection_id }) => {
    return {
      ...state,
      id: session_id,
      collection_id: collection_id,
      messages: messages
    };
  }),
  // Update input text box with user's message
  on(updateUserPrompt, (state, { prompt }) => ({
    ...state,
    userPrompt: prompt
  })),
  // Send message to LLM
  on(sendMessage, (state, { session_id, message }) => {
    const newMessage: ChatMessage = {
      content: [{ text: message }],
      role: 'user' as const,
      timestamp: Date.now()
    };
    return {
      ...state,
      id: session_id,
      messages: [...state.messages, newMessage], // display user chat bubble message
      userPrompt: message, // display the user's message in the prompt
      loading: true
    };
  }),
  // Receive message from LLM
  on(receiveMessage, (state, { response }) => {
    console.log('Session id:', response.session_id);
    // Bypass typing effect - add full message immediately to enable fade-in animation
    const assistantMessage: ChatMessage = {
      content: [{ text: response.response }],
      role: 'assistant' as const,
      timestamp: Date.now()
    };
    return {
      ...state,
      id: response.session_id,
      collection_id: response.collection_id,
      isTyping: false,
      replyFromLLM: null,
      messages: [...state.messages, assistantMessage],
      sourceDocuments: response.source_documents,
      loading: false,
      userPrompt: ''
    };
  }),
  // Typing effect
  on(appendCharacter, (state, { character }) => {
    if (!state.replyFromLLM) return state;

    return {
      ...state,
      replyFromLLM: {
        ...state.replyFromLLM,
        content: [{ text: state.replyFromLLM.content[0].text + character }]
      }
    };
  }),
  // Typing effect
  on(completeTypingMessage, state => {
    if (!state.replyFromLLM) return state;

    return {
      ...state,
      messages: [...state.messages, state.replyFromLLM],
      isTyping: false,
      replyFromLLM: null,
      loading: false,
      userPrompt: '' // clear user prompt
    };
  }),
  // Load documents
  on(loadSourceDocuments, (state, { source_documents }) => ({
    ...state,
    sourceDocuments: source_documents
  }))
);
